import { useEffect, useState } from 'react';

import { getSign, thumbUrl, type SignOfTheDay } from '@/api';
import { useCloudSynced } from '@/hooks';
import { useLang } from '@/lang';
import { QuestionOptions } from '@/pages/practice/QuestionOptions';
import { haptic } from '@/platform';
import { useSavedSigns } from '@/savedSigns';
import { dayNumber } from '@/signOfDay';
import { cn } from '@/lib/utils';
import { useT } from '@/str';

type Verdict = 'knew' | 'missed';

interface DayLog {
  day: number;
  verdict: Verdict;
  code?: string;
  picked?: string;
}

const LOG_KEY = 'sign_of_day';

const VERDICTS: Verdict[] = ['knew', 'missed'];

function parseLog(raw: unknown): DayLog | null {
  if (typeof raw !== 'string') return null;
  try {
    const v: unknown = JSON.parse(raw);
    if (typeof v !== 'object' || v === null) return null;
    const { day, verdict, code, picked } = v as Partial<DayLog>;
    if (typeof day !== 'number') return null;
    if (verdict !== 'knew' && verdict !== 'missed') return null;
    return {
      day,
      verdict,
      ...(typeof code === 'string' && { code }),
      ...(typeof picked === 'string' && { picked }),
    };
  } catch {
    return null;
  }
}

function answers(log: DayLog, sign: SignOfTheDay): boolean {
  if (log.code !== undefined) return log.code === sign.code;
  return log.picked === undefined || sign.choices.includes(log.picked);
}

const RESULT_MS = 3000;
const LEAVE_MS = 250;

const SOD_BTN =
  'min-h-11.5 rounded-ctl border border-border-strong bg-transparent font-bold text-foreground hover:bg-muted active:bg-muted active:transform-none disabled:cursor-default disabled:hover:bg-transparent data-dim:opacity-45 data-picked:border-primary data-picked:bg-primary data-picked:text-primary-foreground data-picked:hover:bg-primary';

export function SignOfDay({ sign }: { sign: SignOfTheDay }) {
  const t = useT();
  const { lang } = useLang();
  const { has, toggle } = useSavedSigns();
  const [revealed, setRevealed] = useState(false);
  const [log, setLog] = useCloudSynced<DayLog | null>(LOG_KEY, parseLog, null, (v) =>
    JSON.stringify(v),
  );
  const [loaded, setLoaded] = useState<{ key: string; text: string | null } | null>(null);
  const [answeredNow, setAnsweredNow] = useState(false);
  const [phase, setPhase] = useState<'shown' | 'leaving' | 'gone'>('shown');

  const today = dayNumber(new Date());
  const mine = log !== null && log.day === today && answers(log, sign) ? log : null;
  const verdict = mine?.verdict ?? null;
  const picked = mine?.picked ?? null;
  const quiz = sign.choices.length > 1;
  const open = revealed || verdict !== null;
  const descKey = `${sign.code}|${lang}`;
  const description = loaded?.key === descKey ? loaded.text : null;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void getSign(sign.code, lang)
      .then((full) => {
        if (!cancelled)
          setLoaded({
            key: `${sign.code}|${lang}`,
            text: full.simple_description || full.description,
          });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, sign.code, lang]);

  useEffect(() => {
    if (!answeredNow) return;
    const leave = setTimeout(() => setPhase('leaving'), RESULT_MS);
    const gone = setTimeout(() => setPhase('gone'), RESULT_MS + LEAVE_MS);
    return () => {
      clearTimeout(leave);
      clearTimeout(gone);
    };
  }, [answeredNow]);

  const answer = (v: Verdict, choice?: string) => {
    setAnsweredNow(true);
    if (v === 'missed' && !has(sign.code)) toggle(sign.code);
    setLog({
      day: today,
      verdict: v,
      code: sign.code,
      ...(choice !== undefined && { picked: choice }),
    });
  };

  const choose = (index: number) => {
    const choice = sign.choices[index];
    const right = choice === sign.name;
    haptic.notify(right ? 'success' : 'error');
    answer(right ? 'knew' : 'missed', choice);
  };

  const label = sign.name || sign.code;

  if (phase === 'gone' || (verdict !== null && !answeredNow)) return null;

  return (
    <section
      data-block="sod"
      data-leaving={phase === 'leaving' || undefined}
      className="bg-card border-border rounded-card ease-close flex flex-col gap-3 border px-4 py-3.75 transition-[opacity,transform] duration-250 data-leaving:-translate-y-1 data-leaving:opacity-0"
    >
      <div className="flex items-baseline justify-between gap-2.5">
        <span className="text-t11 text-faint tracking-kicker font-bold uppercase">
          {t.signOfDay}
        </span>
        <span
          data-tag={verdict ?? 'today'}
          className="text-t11h text-faint data-[tag=knew]:text-ok data-[tag=missed]:text-bad font-bold tabular-nums"
        >
          {verdict === 'knew'
            ? quiz
              ? t.sodRight
              : t.sodKnew
            : verdict === 'missed'
              ? t.sodMissed
              : t.sodToday}
        </span>
      </div>

      <div className="flex items-center gap-3.5">
        {sign.image_url ? (
          <span className="rounded-ctl border-border flex size-16.5 flex-none items-center justify-center overflow-hidden border bg-white p-2">
            <img
              src={thumbUrl(sign.image_url)}
              alt=""
              loading="lazy"
              className="max-h-full max-w-full object-contain"
            />
          </span>
        ) : (
          <span className="rounded-ctl border-border-strong bg-muted flex size-16.5 flex-none flex-col items-center justify-center gap-0.5 border-[1.5px] border-dashed text-center">
            <span className="text-t13 text-muted-foreground font-extrabold tabular-nums">
              {sign.code}
            </span>
            <span className="text-t10h text-faint leading-[1.1] font-bold">{t.signArt}</span>
          </span>
        )}
        <span className="flex min-w-0 flex-1 flex-col gap-0.75">
          <span className="text-t15 font-extrabold tracking-[-0.01em] text-pretty">
            {open ? label : quiz ? t.signQuizPrompt : t.signPrompt}
          </span>
          {open && description && (
            <span className="animate-fade-in text-t12h text-muted-foreground leading-[1.45] text-pretty">
              {description}
            </span>
          )}
        </span>
      </div>

      {quiz ? (
        <QuestionOptions
          answers={sign.choices.map((choice, i) => ({
            answer_index: i,
            answer_text: choice,
            is_correct: choice === sign.name,
          }))}
          answered={verdict !== null}
          selected={picked === null ? null : sign.choices.indexOf(picked)}
          onChoose={choose}
          label={t.signQuizPrompt}
        />
      ) : open ? (
        <div className="flex gap-2.25">
          {VERDICTS.map((v) => (
            <button
              key={v}
              type="button"
              disabled={verdict !== null}
              data-picked={verdict === v || undefined}
              data-dim={(verdict !== null && verdict !== v) || undefined}
              className={cn(SOD_BTN, 'text-t13 min-w-0 flex-1')}
              onClick={() => answer(v)}
            >
              {v === 'knew' ? t.knewIt : t.missedIt}
            </button>
          ))}
        </div>
      ) : (
        <button
          type="button"
          className={cn(SOD_BTN, 'text-t13h w-full')}
          onClick={() => setRevealed(true)}
        >
          {t.showAnswer}
        </button>
      )}

      {verdict !== null && (
        <p className="text-t12 text-faint m-0 font-semibold text-pretty">
          {verdict === 'knew' ? t.signKnewLog : t.signMissLog}
        </p>
      )}
    </section>
  );
}
